import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import { validate } from "uuid";
import { queryBox, queryRadius } from "../box2dUtils";
import { Antibody, ElementInfo, Flashing, Health, JustCombined, Moved, Pop, PreviewOnly, Transform } from "../components";
import { addElementStat, getCompStat, getElementStat, getEntityPlayer, shouldPromote, validatePlacementOrCombination } from "../composition";
import { ShopConstants } from "../constants";
import { ElementStats } from "../elementDisplayInfo";
import { destroyEntity, getEntityById, getId } from "../entityFactory";
import { Game } from "../game";
import { GameElement } from "../gameElementTypes";
import { canCombine, Player } from "../player";
import { addComponent } from "../utils";

export class CombineSystem extends System {

	game: Game;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number, time: number) {
		if (this.game.inSimulation) return;

		//Clean up last frame combine tags
		while (this.queries.wasJustCombined.results.length > 0) {
			this.queries.wasJustCombined.results[0].removeComponent(JustCombined);
		};

		const combines = new Map<number, { player: Player, id: number }>();

		//We can take advantage of the fact that the Moved component is removed after both movement and purchase
		this.queries.moved.removed?.forEach(entity => {
			const player = getEntityPlayer(entity, this.game);
			if (!player) return;
			const combinerID = getId(entity);
			if (!combinerID) return;

			const receiverId = this.checkForCombineOverlap(entity);
			if (receiverId === null) return;
			const receiver = getEntityById(receiverId)
			if (!receiver || receiver.hasComponent(Moved)) return

			if (!combines.has(combinerID) && !combines.has(receiverId)) {
				combines.set(combinerID, { player, id: receiverId });
			}
		});

		const combinePairs = Array.from(combines.entries());
		for (const [combiner, base] of combinePairs) {
			this.combine(base.player, base.id, combiner);
		}
	}

	combine(player: Player, baseId: number, combineId: number) {
		let base = this.game.getCompElementById(baseId);
		if (!base) {
			console.warn(`CombineSystem: Player ${player.id} made invalid combine request`);
			return;
		};

		const targetIsWildcardPromoted = base.type == GameElement.WILDCARD && base.promoted;
		if (base.promoted && !targetIsWildcardPromoted) {
			console.warn(`CombineSystem: Player ${player.id} tried to promote already promoted immune cell ${base.id}`);
			return;
		}

		let combinerPoints: number;
		let combiner = this.game.getCompElementById(combineId);
		if (!combiner || !canCombine(player, base, combiner)) {
			console.warn(`CombineSystem: Player ${player.id} made invalid combine request`);
			return;
		}

		// Swap the wildcard with the other cell
		if (base.type == GameElement.WILDCARD) {
			const temp = combiner;
			combiner = base;
			base = temp;

			const tempId = combineId;
			combineId = baseId;
			baseId = tempId;
		}

		//Preserve any stat buffs that were on the combined cell
		if (combiner.changedStats) {
			for (let stat in combiner.changedStats) {
				if (stat == 'health') continue; //Health is handled separately (see below
				const value = combiner.changedStats[stat as keyof ElementStats]!
				addElementStat(base, (stat as keyof ElementStats), value);
			}
		}
		const health = getCompStat(combiner, 'health');
		addElementStat(base, 'health', health);

		//destroy combined cell (do NOT release from antibody pool, this must happen when combined cell is sold)
		const combinedEntity = getEntityById(combineId);
		if (combinedEntity) {
			destroyEntity(combinedEntity);
		}
		this.game.removeCompositionElement(player.immuneCells, combineId);

		let addedPoints = 1;

		const combinerIsWildcardPromoted = combiner.type == GameElement.WILDCARD && combiner.promoted;
		if (targetIsWildcardPromoted || combinerIsWildcardPromoted) {
			addedPoints = 2;
		}
		combinerPoints = combiner.combined + addedPoints;

		//upgrade combine target TODO: Needs to actually do a triple
		base.combined += combinerPoints;
		base.combined = Math.min(base.combined, ShopConstants.PromotionNumber - 1);

		this.game.stats.cellsCombined += 1;

		const canPromote = shouldPromote(base);
		if (canPromote) {
			base.promoted = true;
			this.game.stats.cellsTripled += 1;
			this.game.onPromote(player, base);
		}
		else {
			this.game.onCombine(player, base);
		}
		// else {
		// 	const combinedBaseHealth = getElementStat(base.type, base.promoted || false, 'health');
		// 	addElementStat(base, 'health', 1);
		// }

		const baseEntity = getEntityById(baseId);
		if (baseEntity) {
			addComponent(baseEntity, JustCombined, { combinedPoints: base.combined })
			if (base) {
				//Nothing will update the healths of this entity, so we need to do it here, it always gains at least 1
				//and more if the combined element was buffed. This is hacky and only matters becasue of how health is displayed
				//TODO: It would be nice if we could destroy and recreate the entity but that would stop sharedOnBuy from using it.
				const ownHp = getCompStat(base, 'health');
				const hp = baseEntity.getComponent(Health);
				if (hp) hp.hp = ownHp;
			}
		}
	}

	checkForCombineOverlap(entity: Entity) {
		const transform = entity.getComponent(Transform);
		const info = entity.getComponent(ElementInfo);
		const player = getEntityPlayer(entity, this.game);
		if (!transform || !player || !ElementInfo) return null;
		const el = this.game.getEntityCompElement(entity);
		const elementDesc = el ? el : { type: info.type } //maybe could be prevdew

		const detectedCombinations = new Array<number>();
		validatePlacementOrCombination(player, transform, elementDesc, this.game, { detectedCombinationTargets: detectedCombinations });
		if (detectedCombinations.length > 0) {
			return detectedCombinations[0];
		}
		return null;
	}
}

CombineSystem.queries = {
	moved: {
		components: [Moved, Antibody, Not(PreviewOnly)],
		listen: { removed: true }
	},
	wasJustCombined: {
		components: [JustCombined],
	}
}